if (!customElements.get('sticky-anchor-bar')) {
  class StickyAnchorBar extends HTMLElement {
    constructor() {
      super()

      this.anchorSections = []
      this.isReady = false

      this.onAnchorItemClickListener = this.onAnchorItemClick.bind(this)
      this.onWindowScrolledListener = this.throttle(this.onWindowScrolled.bind(this), 80)
      this.onAnchorListScrolledListener = this.throttle(this.onAnchorListScrolled.bind(this), 80)
      this.onSectionsChangedListener = this.onSectionsChanged.bind(this)
      this.onNavLeftClickListener = this.onNavLeftClick.bind(this)
      this.onNavRightClickListener = this.onNavRightClick.bind(this)

      this.anchorResizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            this.onAnchorListScrolledListener()
          })
        : null
    }

    get header() {
      return document.querySelector(
        'header.shopify-section--header, header.header, .shopify-section--header'
      )
    }

    getStickyTopOffset() {
      if (!this.header) return 0

      const headerRect = this.header.getBoundingClientRect()

      return Math.max(0, Math.round(headerRect.bottom))
    }

 
    get anchorList() {
      return this.querySelector('.anchor-list')
    }

    get anchorItems() {
      return Array.from(this.querySelectorAll('.anchor-list .anchor-item'))
    }

    get anchorIds() {
      return [
        ...new Set(
          this.anchorItems
            .map(item => item.dataset.anchorId?.trim())
            .filter(Boolean)
        )
      ]
    }

    get navLeft() {
      return this.querySelector('.nav-left')
    }

    get navRight() {
      return this.querySelector('.nav-right')
    }

    throttle(func, delay = 80) {
      let lastCallTime = 0
      let timeoutId = null

      return (...args) => {
        const now = Date.now()
        const remainingTime = delay - (now - lastCallTime)

        if (remainingTime <= 0) {
          if (timeoutId) {
            window.clearTimeout(timeoutId)
            timeoutId = null
          }

          lastCallTime = now
          func(...args)
          return
        }

        if (timeoutId) {
          window.clearTimeout(timeoutId)
        }

        timeoutId = window.setTimeout(() => {
          lastCallTime = Date.now()
          timeoutId = null
          func(...args)
        }, remainingTime)
      }
    }

    connectedCallback() {
      if (this.isReady) return

      this.isReady = true

      this.refreshAnchorItemListeners()
      this.initSectionIdMarker()

      this.navLeft?.addEventListener('click', this.onNavLeftClickListener)
      this.navRight?.addEventListener('click', this.onNavRightClickListener)

      window.addEventListener('scroll', this.onWindowScrolledListener, { passive: true })
      window.addEventListener('resize', this.onAnchorListScrolledListener, { passive: true })
      window.addEventListener('resize', this.onWindowScrolledListener, { passive: true })
      if (this.anchorList) {
        this.anchorList.addEventListener('scroll', this.onAnchorListScrolledListener, { passive: true })
        this.anchorResizeObserver?.observe(this.anchorList)
      }

      document.addEventListener('shopify:section:load', this.onSectionsChangedListener)
      document.addEventListener('shopify:section:unload', this.onSectionsChangedListener)
      document.addEventListener('shopify:section:reorder', this.onSectionsChangedListener)

        if (window.matchMedia('(max-width: 767px)').matches && this.anchorList) {
        this.anchorList.scrollLeft = 0
      }

      this.onAnchorListScrolled()
      this.onWindowScrolled()
    }

    disconnectedCallback() {
      if (!this.isReady) return

      this.isReady = false

      this.anchorItems.forEach(item => {
        item.removeEventListener('click', this.onAnchorItemClickListener)
      })

      this.navLeft?.removeEventListener('click', this.onNavLeftClickListener)
      this.navRight?.removeEventListener('click', this.onNavRightClickListener)

      window.removeEventListener('scroll', this.onWindowScrolledListener)
      window.removeEventListener('resize', this.onAnchorListScrolledListener)
      window.removeEventListener('resize', this.onWindowScrolledListener)
      this.anchorList?.removeEventListener('scroll', this.onAnchorListScrolledListener)

      document.removeEventListener('shopify:section:load', this.onSectionsChangedListener)
      document.removeEventListener('shopify:section:unload', this.onSectionsChangedListener)
      document.removeEventListener('shopify:section:reorder', this.onSectionsChangedListener)

      this.anchorResizeObserver?.disconnect()

      if (this.header) {
        this.header.style.visibility = 'visible'
      }
    }

    refreshAnchorItemListeners() {
      this.anchorItems.forEach(item => {
        item.removeEventListener('click', this.onAnchorItemClickListener)

        if (item.dataset.anchorId?.trim()) {
          item.addEventListener('click', this.onAnchorItemClickListener)
        }
      })
    }

    onSectionsChanged() {
      window.requestAnimationFrame(() => {
        this.refreshAnchorItemListeners()
        this.initSectionIdMarker()
        this.onAnchorListScrolled()
        this.onWindowScrolled()
      })
    }

    onNavLeftClick() {
      if (!this.anchorList) return

      this.anchorList.scrollTo({
        left: this.anchorList.scrollLeft - 100,
        behavior: 'smooth'
      })
    }

    onNavRightClick() {
      if (!this.anchorList) return

      this.anchorList.scrollTo({
        left: this.anchorList.scrollLeft + 100,
        behavior: 'smooth'
      })
    }

    getNextShopifySection(section) {
      let nextSection = section?.nextElementSibling || null

      while (nextSection && !nextSection.classList.contains('shopify-section')) {
        nextSection = nextSection.nextElementSibling
      }

      return nextSection
    }

    hasMeaningfulContent(element) {
      if (!element) return false

      if (element.children.length > 0) {
        return true
      }

      return element.textContent.trim() !== ''
    }

    isStandaloneAnchorMarker(section, anchor) {
      if (!section || !anchor) return false

      if (this.hasMeaningfulContent(anchor)) {
        return false
      }

      return Array.from(section.children).every(child => {
        if (child === anchor) return true

        if (child.matches('style, script, link')) {
          return true
        }

        if (
          child.classList.contains('sticky-anchor') &&
          !this.hasMeaningfulContent(child)
        ) {
          return true
        }

        return false
      })
    }

    resolveTargetSection(sectionAnchor) {
      const currentSection = sectionAnchor?.closest('.shopify-section')

      if (!currentSection) {
        return null
      }

      if (!sectionAnchor.classList.contains('sticky-anchor')) {
        return currentSection
      }

      if (this.isStandaloneAnchorMarker(currentSection, sectionAnchor)) {
        return this.getNextShopifySection(currentSection)
      }

      return currentSection
    }

    initSectionIdMarker() {
      document.querySelectorAll('[section-anchor-id]').forEach(section => {
        section.removeAttribute('section-anchor-id')
      })

      if (this.anchorIds.length === 0) {
        this.anchorSections = []
        return
      }

      const sectionAnchors = Array.from(document.querySelectorAll('[anchor-id]'))
      const anchorSections = []

      for (const anchorId of this.anchorIds) {
        const sectionAnchor = sectionAnchors.find(item => {
          return item.getAttribute('anchor-id')?.trim() === anchorId
        })

        if (!sectionAnchor) continue

        const targetSection = this.resolveTargetSection(sectionAnchor)

        if (!targetSection) continue

        targetSection.setAttribute('section-anchor-id', anchorId)

        if (!anchorSections.includes(targetSection)) {
          anchorSections.push(targetSection)
        }
      }

      this.anchorSections = anchorSections
      this.onScrolledToFindMatchSection()
    }

    // onAnchorItemClick(event) {
    //   const anchorId = event.currentTarget.dataset.anchorId?.trim()

    //   if (!anchorId) return

    //   let scrollView = this.anchorSections.find(section => {
    //     return section.getAttribute('section-anchor-id') === anchorId
    //   })

    //   if (!scrollView) {
    //     this.initSectionIdMarker()

    //     scrollView = this.anchorSections.find(section => {
    //       return section.getAttribute('section-anchor-id') === anchorId
    //     })
    //   }

    //   if (!scrollView) return

    //   const offset = this.getBoundingClientRect().height
    //   const elementPosition = scrollView.getBoundingClientRect().top
    //   const offsetPosition = elementPosition + window.scrollY - offset

    //   window.scrollTo({
    //     top: offsetPosition,
    //     behavior: 'smooth'
    //   })
    // }
    getAnchorSafeGap() {
      return 30
    }
    getMobileAnchorPadding() {
      return 16
    }

    scrollActiveAnchorIntoView(activeItem, activeIndex, previousIndex) {
      if (!activeItem || !this.anchorList) return

      if (!window.matchMedia('(max-width: 767px)').matches) return

      const list = this.anchorList
      const padding = this.getMobileAnchorPadding()
      const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth)

      if (maxScrollLeft <= 0) return

      const itemLeft = activeItem.offsetLeft
      const itemRight = itemLeft + activeItem.offsetWidth
      const visibleLeft = list.scrollLeft + padding
      const visibleRight = list.scrollLeft + list.clientWidth - padding

      const isMovingForward = previousIndex !== -1 && activeIndex > previousIndex
      const isMovingBackward = previousIndex !== -1 && activeIndex < previousIndex

      let targetScrollLeft = list.scrollLeft

      if (isMovingForward) {
        targetScrollLeft = itemLeft - padding
      } else if (isMovingBackward) {
        targetScrollLeft = itemRight - list.clientWidth + padding
      } else if (itemLeft < visibleLeft) {
        targetScrollLeft = itemLeft - padding
      } else if (itemRight > visibleRight) {
        targetScrollLeft = itemRight - list.clientWidth + padding
      } else {
        return
      }

      targetScrollLeft = Math.max(
        0,
        Math.min(targetScrollLeft, maxScrollLeft)
      )

      if (Math.abs(list.scrollLeft - targetScrollLeft) < 2) return

      list.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      })
    }

    setActiveAnchor(anchorId) {
      const items = this.anchorItems
      const previousIndex = items.findIndex(item => {
        return item.classList.contains('active')
      })

      const activeIndex = items.findIndex(item => {
        return item.dataset.anchorId?.trim() === anchorId
      })

      if (activeIndex === -1) return

      items.forEach((item, index) => {
        item.classList.toggle('active', index === activeIndex)
      })

      if (activeIndex === previousIndex) return

      window.requestAnimationFrame(() => {
        this.scrollActiveAnchorIntoView(
          items[activeIndex],
          activeIndex,
          previousIndex
        )
      })
    }
    onAnchorItemClick(event) {
      const anchorId = event.currentTarget.dataset.anchorId?.trim()

      if (!anchorId) return
      this.setActiveAnchor(anchorId)
      let scrollView = this.anchorSections.find(section => {
        return section.getAttribute('section-anchor-id') === anchorId
      })

      if (!scrollView) {
        this.initSectionIdMarker()

        scrollView = this.anchorSections.find(section => {
          return section.getAttribute('section-anchor-id') === anchorId
        })
      }

      if (!scrollView) return

      const stickyTopOffset = this.getStickyTopOffset()
      const stickyBarHeight = this.getBoundingClientRect().height
      const safeGap = this.getAnchorSafeGap()
      const elementPosition = scrollView.getBoundingClientRect().top
      const offsetPosition =
        elementPosition +
        window.scrollY -
        stickyTopOffset -
        stickyBarHeight -
        safeGap

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
    onWindowScrolled() {
      this.onScrolledForAuto()
      this.onScrolledToFindMatchSection()
    }

    onScrolledToFindMatchSection() {
      const barHeight = this.getBoundingClientRect().height
      const stickyTopOffset = this.classList.contains('pin')
        ? this.getStickyTopOffset()
        : 0

      const triggerLine =
      stickyTopOffset +
      barHeight +
      this.getAnchorSafeGap() +
      1

      const visibleSection = this.anchorSections
        .map(section => {
          return {
            section,
            top: section.getBoundingClientRect().top
          }
        })
        .filter(item => item.top <= triggerLine)
        .sort((a, b) => b.top - a.top)[0]?.section

      const sectionAnchorId = visibleSection?.getAttribute('section-anchor-id')

      if (!sectionAnchorId) return

      this.setActiveAnchor(sectionAnchorId)
    }
    onScrolledForAuto() {
      const stickyTopOffset = this.getStickyTopOffset()
      const shouldPin = this.getBoundingClientRect().top <= stickyTopOffset

      this.classList.toggle('pin', shouldPin)
      this.style.setProperty('--sticky-bar-top', `${stickyTopOffset}px`)

      if (this.header) {
        this.header.style.visibility = 'visible'
      }
    }

    onAnchorListScrolled() {
      if (!this.anchorList) return

      if (this.anchorList.scrollWidth <= this.anchorList.clientWidth) {
        this.setAttribute('anchor-scroll-pos', 'none')
        return
      }

      if (this.anchorList.scrollLeft <= 0) {
        this.setAttribute('anchor-scroll-pos', 'left')
        return
      }

      if (
        this.anchorList.scrollLeft + this.anchorList.clientWidth >=
        this.anchorList.scrollWidth - 1
      ) {
        this.setAttribute('anchor-scroll-pos', 'right')
        return
      }

      this.setAttribute('anchor-scroll-pos', 'center')
    }
  }

  customElements.define('sticky-anchor-bar', StickyAnchorBar)
}